import { useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/browser';

const isDebug = window.__ENV__?.SENTRY_DEBUG === 'true';
const hasDsn  = !!(window.__ENV__?.SENTRY_DSN);

// ── Transport interceptor — hooks both XHR and fetch for Sentry host ─────────
const xhrLog = []; // shared mutable list

function getSentryHost() {
    try { return new URL(window.__ENV__?.SENTRY_DSN || '').hostname; } catch (_) { return ''; }
}

function pushEntry(entry, onEntry) {
    xhrLog.unshift(entry);
    if (xhrLog.length > 20) xhrLog.pop();
    onEntry([...xhrLog]);
}

function hookSentryXHR(onEntry) {
    const sentryHost = getSentryHost();
    if (!sentryHost) return;

    const origOpen = XMLHttpRequest.prototype.open;
    const origSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function (method, url, ...rest) {
        this._isSentry = typeof url === 'string' && url.includes(sentryHost);
        this._sentryUrl = url;
        return origOpen.call(this, method, url, ...rest);
    };

    XMLHttpRequest.prototype.send = function (...args) {
        if (this._isSentry) {
            const ts = new Date().toLocaleTimeString();
            const entry = { ts, url: this._sentryUrl, status: '…', body: '' };
            xhrLog.unshift(entry);
            if (xhrLog.length > 20) xhrLog.pop();

            this.addEventListener('load', () => {
                entry.status = String(this.status);
                entry.body   = this.responseText?.slice(0, 120) || '';
                onEntry([...xhrLog]);
            });
            this.addEventListener('error', () => {
                entry.status = 'net-err';
                onEntry([...xhrLog]);
            });
        }
        return origSend.call(this, ...args);
    };
}

function hookSentryCapture(onEntry) {
    const orig = Sentry.captureException;
    Sentry.captureException = function (err) {
        const ts  = new Date().toLocaleTimeString();
        const msg = err?.message || String(err);
        const entry = { ts, url: `captureException("${msg.slice(0, 60)}")`, status: 'SDK', body: '' };
        xhrLog.unshift(entry);
        if (xhrLog.length > 20) xhrLog.pop();
        onEntry([...xhrLog]);
        return orig.apply(this, arguments);
    };
}

function hookSentryFetch(onEntry) {
    const sentryHost = getSentryHost();
    if (!sentryHost || !window.fetch) return;

    const origFetch = window.fetch;
    window.fetch = function (input, init) {
        const url = typeof input === 'string' ? input : input?.url || '';
        if (!url.includes(sentryHost)) return origFetch.call(this, input, init);

        const ts    = new Date().toLocaleTimeString();
        const entry = { ts, url, status: '…', body: '' };
        xhrLog.unshift(entry);
        if (xhrLog.length > 20) xhrLog.pop();
        onEntry([...xhrLog]);

        return origFetch.call(this, input, init).then(
            async res => {
                entry.status = String(res.status);
                try {
                    const clone = res.clone();
                    entry.body = (await clone.text()).slice(0, 120);
                } catch (_) {}
                onEntry([...xhrLog]);
                return res;
            },
            err => {
                entry.status = 'net-err';
                entry.body   = String(err);
                onEntry([...xhrLog]);
                throw err;
            },
        );
    };
}

// ── Styles ───────────────────────────────────────────────────────────────────
const S = {
    badge: {
        position: 'fixed',
        bottom: 16,
        right: 16,
        zIndex: 9999,
        padding: '4px 10px',
        borderRadius: 20,
        fontSize: 12,
        fontFamily: 'monospace',
        fontWeight: 600,
        color: '#fff',
        boxShadow: '0 2px 6px rgba(0,0,0,0.35)',
        cursor: 'pointer',
        userSelect: 'none',
    },
    panel: {
        position: 'fixed',
        bottom: 48,
        right: 16,
        zIndex: 9998,
        width: 320,
        maxHeight: 380,
        overflowY: 'auto',
        background: '#1e1e1e',
        color: '#d4d4d4',
        fontFamily: 'monospace',
        fontSize: 11,
        borderRadius: 8,
        boxShadow: '0 4px 16px rgba(0,0,0,0.5)',
        padding: '8px 10px',
    },
    row: { borderBottom: '1px solid #333', padding: '4px 0', wordBreak: 'break-all' },
    ok:      { color: '#4ec94e' },
    err:     { color: '#f47272' },
    pending: { color: '#aaa' },
};

function statusColor(s) {
    if (s === 'SDK')  return { color: '#c586c0' };
    if (s === '…') return S.pending;
    if (s.startsWith('2'))  return S.ok;
    return S.err;
}

function badgeBg(log) {
    if (!hasDsn) return '#e65100';
    if (!log.length) return '#888';
    const last = log[0].status;
    if (last === '…') return '#888';
    if (last.startsWith('2')) return '#2e7d32';
    return '#c62828';
}

function badgeLabel(log) {
    if (!hasDsn) return 'Sentry: no DSN';
    if (!log.length) return 'Sentry: waiting…';
    const s = log[0].status;
    if (s === '…') return 'Sentry: sending…';
    if (s.startsWith('2')) return `Sentry: OK (${s}) ✓`;
    return `Sentry: ${s} ✗`;
}

// ── Component ────────────────────────────────────────────────────────────────
export default function SentryDebugBadge() {
    const [log, setLog]       = useState([]);
    const [open, setOpen]     = useState(false);
    const [initInfo, setInitInfo] = useState('');
    const hookedRef           = useRef(false);

    useEffect(() => {
        if (!isDebug) return;

        // Gather init diagnostics
        const client = Sentry.getCurrentHub().getClient();
        const dsn = window.__ENV__?.SENTRY_DSN || '';
        const info = [
            `DSN: ${dsn ? dsn.replace(/\/\/[^@]+@/, '//***@') : '—'}`,
            `client: ${client ? 'yes' : 'NO (not initialized)'}`,
            `hasDsn: ${hasDsn}`,
        ].join(' | ');
        setInitInfo(info);

        // Install transport hooks once (before sending)
        if (!hookedRef.current) {
            hookedRef.current = true;
            hookSentryCapture(entries => setLog(entries));
            hookSentryXHR(entries => setLog(entries));
            hookSentryFetch(entries => setLog(entries));
        }

        // v5 API: check client on hub
        if (!hasDsn || !client) return;
    }, []);

    if (!isDebug) return null;

    return (
        <>
            {open && (
                <div style={S.panel}>
                    <div style={{ marginBottom: 6, color: '#888', fontSize: 10 }}>
                        {initInfo}
                    </div>
                    {log.length === 0 && (
                        <div style={{ color: '#f0a500' }}>
                            No Sentry HTTP requests intercepted yet.<br />
                            (fetch transport active — hooked on mount)
                        </div>
                    )}
                    {log.map((e, i) => (
                        <div key={i} style={S.row}>
                            <span style={{ color: '#888' }}>{e.ts} </span>
                            <span style={statusColor(e.status)}>[{e.status}] </span>
                            <span style={{ color: '#9cdcfe' }}>{e.url?.split('/').slice(-2).join('/') || e.url}</span>
                            {e.body && (
                                <div style={{ color: '#ce9178', marginTop: 2 }}>{e.body}</div>
                            )}
                        </div>
                    ))}
                    <div style={{ marginTop: 6, color: '#555', fontSize: 10 }}>
                        tap badge to close · {log.length} requests
                    </div>
                </div>
            )}
            <div
                style={{ ...S.badge, backgroundColor: badgeBg(log) }}
                onClick={() => setOpen(o => !o)}
            >
                {badgeLabel(log)}
            </div>
        </>
    );
}
